import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Level from '../PLG/Level';
import AuthModal from '../auth/AuthModal';
import { useNavigation } from '../../contexts/NavigationContext';
import useMediaQuery from '../../hooks/useMediaQuery';

export default function Header({ user, onLogin, onLogout }) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [reviewCount, setReviewCount] = useState(0);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authView, setAuthView] = useState('sign_in');
  const { sidebarCollapsed } = useNavigation();
  const isDesktop = useMediaQuery('(min-width: 1024px)');

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    async function fetchReviewCount() {
      if (!user) {
        setReviewCount(0);
        return;
      }
      try {
        const API_BASE = process.env.REACT_APP_API_BASE;
        const API_KEY = process.env.REACT_APP_API_KEY;
        const response = await fetch(`${API_BASE}/v1/reviews/me`, {
          headers: {
            'X-API-Key': API_KEY,
            'Content-Type': 'application/json',
            'X-User-ID': user.id
          },
        });
        if (!response.ok) throw new Error('Failed to fetch reviews');
        const data = await response.json();
        setReviewCount(Array.isArray(data) ? data.length : 0);
      } catch {
        setReviewCount(0);
      }
    }
    fetchReviewCount();
  }, [user]);

  useEffect(() => {
    if (user && authModalOpen) {
      setAuthModalOpen(false);
    }
  }, [user, authModalOpen]);

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  function getInitials(text) {
    if (!text) return '';
    return text.charAt(0).toUpperCase();
  }

  function openAuthModal(view) {
    setAuthView(view);
    setAuthModalOpen(true);
  }

  // Calculate sidebar offset dynamically
  const sidebarOffset = isDesktop && user ? (sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64') : '';

  return (
    <header className="bg-black shadow-sm sticky top-0 z-50">
      <div className={`px-4 lg:px-8 py-4 flex justify-between items-center transition-all duration-300 ${sidebarOffset}`}>
        <h1 className="text-2xl font-bold">
            {user ? (
            <Link to="/">
              <img src="/header-nav.png" alt="Clinfilm logo" className="h-12 w-auto" />
            </Link>
          ) : null}
        </h1>
        <nav>
          <ul className="flex space-x-6 items-center text-white">
            {user ? (
              <>
                <li>
                  <Level count={reviewCount} />
                </li>
                <li className="relative" ref={dropdownRef}>
                  <button
                    onClick={toggleDropdown}
                    className="flex items-center space-x-2 gradient-bg text-white rounded-full w-10 h-10 justify-center font-semibold select-none"
                    aria-haspopup="true"
                    aria-expanded={dropdownOpen}
                  >
                    <span className="sr-only">Open user menu</span>
                    {getInitials(user.name || user.email)}
                  </button>
                  {dropdownOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-black rounded-md shadow-lg border border-gray-700 py-1 z-50">
                      <Link
                        to="/profile"
                        className="block px-4 py-2 text-white hover:bg-indigo-700"
                        onClick={() => setDropdownOpen(false)}
                      >
                        Profile
                      </Link>
                      <button
                        onClick={() => {
                          setDropdownOpen(false);
                          onLogout();
                        }}
                        className="w-full text-left px-4 py-2 text-white hover:bg-indigo-700"
                      >
                        Logout
                      </button>
                    </div>
                  )}
                </li>
              </>
            ) : (
              <>
                <li>
                  <span
                    className="font-semibold cursor-pointer"
                    onClick={() => openAuthModal('sign_in')}
                  >
                    Login
                  </span>
                </li>
                <li>
                  <button
                    onClick={() => openAuthModal('sign_up')}
                    className="brand-button"
                  >
                    Join Us
                  </button>
                </li>
              </>
            )}
          </ul>
        </nav>
      </div>
      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        view={authView}
      />
    </header>
  );
}

